import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ChevronRight, MessageCircle, Plane, Radar, Target, Gauge } from "lucide-react";
import { Button } from "@/components/ui/button";

const HeroSection = ({ settings }) => {
  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Dark Overlay */}
      <div className="hero-bg absolute inset-0" />
      
      {/* Grid Background */}
      <div className="absolute inset-0 grid-bg opacity-30" />
      
      {/* Glow Effect */}
      <div className="hero-glow" />

      {/* Animated Plane - straight horizontal flight */}
      <div className="absolute top-1/3 left-0 plane-fly">
        <Plane className="w-16 h-16 text-primary/30" />
      </div>

      {/* HUD Elements - Top Left */}
      <motion.div
        initial={{ opacity: 0, x: -50 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 0.5 }}
        className="absolute top-24 left-6 md:left-12 hidden md:block"
      >
        <div className="hud-data flex items-center gap-2 mb-2">
          <Gauge className="w-4 h-4" />
          <span>ALT: 35,000 FT</span>
        </div>
        <div className="hud-data flex items-center gap-2">
          <Target className="w-4 h-4" />
          <span>HDG: 270</span>
        </div>
      </motion.div>

      {/* HUD Elements - Top Right */}
      <motion.div
        initial={{ opacity: 0, x: 50 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 0.5 }}
        className="absolute top-24 right-6 md:right-12 hidden md:block"
      >
        <div className="hud-data text-right flex items-center gap-2 justify-end mb-2">
          <span>SPD: 450 KTS</span>
        </div>
        <div className="hud-data text-right flex items-center gap-2 justify-end">
          <span>MACH: 0.85</span>
        </div>
      </motion.div>

      {/* Radar Element - Bottom Right */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1 }}
        className="absolute bottom-24 right-12 hidden lg:block"
      >
        <div className="relative w-32 h-32">
          <div className="absolute inset-0 border border-primary/20 rounded-full" />
          <div className="absolute inset-4 border border-primary/15 rounded-full" />
          <div className="absolute inset-8 border border-primary/10 rounded-full" />
          <div className="absolute top-1/2 left-1/2 w-1 h-1 bg-primary rounded-full transform -translate-x-1/2 -translate-y-1/2" />
          <div 
            className="absolute top-1/2 left-1/2 w-16 h-[1px] bg-gradient-to-r from-primary/80 to-transparent origin-left radar-sweep"
            style={{ transformOrigin: '0 50%' }}
          />
          <Radar className="absolute top-1/2 left-1/2 w-4 h-4 text-primary/50 transform -translate-x-1/2 -translate-y-1/2" />
        </div>
      </motion.div>

      {/* Main Content */}
      <div className="relative z-10 max-w-5xl mx-auto px-6 md:px-12 text-center">
        {/* Kicker */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mb-6"
        >
          <span className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 border border-primary/30 rounded-full text-primary text-xs uppercase tracking-widest">
            <span className="w-2 h-2 bg-primary rounded-full pulse-glow" />
            Premium FiveM Scripts
          </span>
        </motion.div>

        {/* Main Headline */}
        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight uppercase mb-6 text-glow"
          data-testid="hero-title"
        >
          {settings?.hero_title || "Premium Aviation Scripts for FiveM"}
        </motion.h1>

        {/* Subtitle */}
        <motion.p
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="text-base md:text-lg text-muted-foreground max-w-2xl mx-auto mb-10 leading-relaxed"
          data-testid="hero-subtitle"
        >
          {settings?.hero_subtitle || "The highest quality, optimized aviation systems for your roleplay servers. Realistic physics, professional support, and continuous updates."}
        </motion.p>

        {/* CTA Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="flex flex-col sm:flex-row items-center justify-center gap-4"
        >
          <Link to="/products">
            <Button
              data-testid="hero-products-btn"
              className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-lg border-l-4 border-accent uppercase tracking-widest font-bold text-sm px-8 py-6 glow-blue-hover btn-mechanical"
            >
              Browse Products
              <ChevronRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
          {settings?.discord_url && (
            <a href={settings.discord_url} target="_blank" rel="noopener noreferrer">
              <Button
                data-testid="hero-discord-btn"
                variant="outline"
                className="bg-transparent border border-primary/30 text-primary hover:bg-primary/10 rounded-lg uppercase tracking-widest text-sm px-8 py-6"
              >
                <MessageCircle className="w-5 h-5 mr-2" />
                Join Discord
              </Button>
            </a>
          )}
        </motion.div>

        {/* Stats */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className="mt-16 grid grid-cols-3 gap-8 max-w-2xl mx-auto"
        >
          {settings?.show_stats === true && [
            { value: settings.stat_customers, label: settings.stat_customers_label },
            { value: settings.stat_scripts, label: settings.stat_scripts_label },
            { value: settings.stat_support, label: settings.stat_support_label },
          ].map((stat, index) => (
            <div key={index} className="text-center">
              <div className="stat-counter">{stat.value}</div>
              <div className="text-xs uppercase tracking-widest text-muted-foreground mt-1">
                {stat.label}
              </div>
            </div>
          ))}
        </motion.div>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1 }}
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
      >
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="flex flex-col items-center gap-2"
        >
          <span className="text-xs uppercase tracking-widest text-muted-foreground">
            Explore
          </span>
          <div className="w-[1px] h-8 bg-gradient-to-b from-primary to-transparent" />
        </motion.div>
      </motion.div>
    </section>
  );
};

export default HeroSection;
